import type { OpOutcome } from './types'

/**
 * The conflict classifier (doc §7): map a replayed op's HTTP response onto
 * the outcome lattice. The domain is append/terminal-first, so most 409s
 * are terminal-state conflicts ("someone else completed it") whose server
 * state rides in error.details — never silently discard the captured work.
 */

const TERMINAL_CODES =
  /ALREADY_|_LOCKED|_RETAINED|_TERMINAL|THREAD_RESOLVED|^LEGAL_HOLD$|^RETENTION_FLOOR$|^USE_CORRECTION_PATH$|^COMPANY_DELETE_DISABLED$/
const STALE_CODES = /^(STALE_UPDATE|VERSION_CONFLICT|CONFLICT)$/
const REFERENCE_CODES = /^(INVALID_REFERENCE|NOT_FOUND|EQUIPMENT_RETIRED)$/

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown[]; requestId?: string }
}

/**
 * Did the API itself run and fail on THIS request (FF-AUDIT-069)?
 *
 * A FloorFile error envelope on a 5xx means the Worker executed the handler
 * and threw — a deterministic bug for this input, not a flaky link. Retrying
 * it forever with the same body cannot succeed; the audit's help request sat
 * `QUEUED · 5 attempts · INTERNAL` for an hour while the person believed
 * supervisors had been told. A bare 502/503/504 from a proxy or a cold
 * Worker, a 408/425/429, or no body at all, IS the flaky-network case and
 * keeps the full retry budget.
 */
export function isApplicationFailure(status: number, body: unknown): boolean {
  if (status < 500) return false
  const code = (body as ErrorEnvelope)?.error?.code
  return typeof code === 'string' && code.length > 0 && status !== 502 && status !== 503 && status !== 504
}

/**
 * Should the DIRECT (online) path fall back to the outbox for this response?
 * Only for infrastructure answers — the ones a retry can fix. An application
 * failure must reach the person as an error, not vanish into a queue.
 */
export function isInfrastructureFailure(status: number, body: unknown): boolean {
  if (status === 408 || status === 425 || status === 429) return true
  if (status < 500) return false
  return !isApplicationFailure(status, body)
}

/**
 * The rejection a direct-path mutation throws for a non-OK answer that is NOT
 * an infrastructure failure. Shaped like the SDK's `ApiError` (`code`,
 * `status`, `details`, `requestId`) without depending on the SDK: this package
 * cannot import it, but the web's error resolver and its field-issue mapper
 * key on exactly those properties. Before FF-AUDIT-005 only `status` and
 * `code` survived, so a 422's per-field `details` never reached the form that
 * could have pointed at the answer.
 */
export function envelopeError(status: number, body: unknown): Error & { status: number; code?: string; details?: unknown[]; requestId?: string } {
  const err = (body as ErrorEnvelope | undefined)?.error
  return Object.assign(new Error(err?.message ?? `Request failed (${status})`), {
    name: 'ApiError',
    status,
    ...(err?.code ? { code: err.code } : {}),
    ...(Array.isArray(err?.details) ? { details: err.details } : {}),
    ...(typeof err?.requestId === 'string' ? { requestId: err.requestId } : {}),
  })
}

export function classifyResponse(status: number, body: unknown, replayHeader?: string | null): OpOutcome {
  if (status >= 200 && status < 300) {
    return { type: 'applied', body, replayed: replayHeader === 'true' }
  }
  if (status === 401) return { type: 'auth' }
  if (status === 408 || status === 425 || status === 429 || status >= 500) {
    const code = (body as ErrorEnvelope)?.error?.code ?? String(status)
    // An application failure still gets a few retries (a transient DB error
    // also arrives as INTERNAL) but dead-letters quickly, so the sync center
    // shows it with Retry/Discard instead of a queue that never drains.
    return isApplicationFailure(status, body)
      ? { type: 'retryable', error: code, appError: true }
      : { type: 'retryable', error: code }
  }

  const err = (body as ErrorEnvelope)?.error
  const code = err?.code ?? `HTTP_${status}`
  const message = err?.message ?? 'The server rejected this action.'
  const serverState = Array.isArray(err?.details) ? err.details[0] : undefined

  if (status === 409 && STALE_CODES.test(code)) {
    return { type: 'conflict', kind: 'stale', code, message, serverState }
  }
  if (TERMINAL_CODES.test(code)) {
    return { type: 'conflict', kind: 'terminal', code, message, serverState }
  }
  if (status === 404 || REFERENCE_CODES.test(code)) {
    // Reference-now-invalid (§7): held with a fix-up prompt, not dropped.
    return { type: 'conflict', kind: 'invalid-ref', code, message, serverState }
  }
  return { type: 'permanent', code, message }
}

/** Retry backoff: 5 s · 2^attempts, capped at 5 minutes (doc §5). */
export function backoffMs(attempts: number): number {
  return Math.min(5_000 * 2 ** attempts, 300_000)
}
